import { useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Spinner } from '../../components/atoms/Spinner'
import { Paginator } from '../../components/molecules/Paginator'
import { CustomDataTypeCard } from './CustomDataTypeCard'
import { CustomDataTypeFormModal } from './CustomDataTypeFormModal'
import { useCustomDataTypes } from '../../../viewmodels/useCustomDataTypes'
import { useAuth } from '../../../context/AuthContext'
import { SCREENCODES } from '../../../constants/screen.constants'

/**
 * /manage/custom-data-types — user-defined enumerations.
 *
 * Concept ported from Ridgegap (c:/PROJECTS/ridgegap —
 * ManageCustomTypesScreen.tsx). Card-based expandable list; rename
 * inline; delete via window.confirm (matches AttributesPage). The
 * Add-type modal is the only popup form — value-level operations live
 * inside each card's expanded body.
 */
export function CustomDataTypesPage(): JSX.Element {
  const {
    items,
    total,
    page,
    pageSize,
    isLoading,
    error,
    setPage,
    reload,
    createType,
    updateType,
    deleteType,
    addValue,
    renameValue,
    deleteValue
  } = useCustomDataTypes()
  const { can } = useAuth()
  const [showAdd, setShowAdd] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [opError, setOpError] = useState<string | null>(null)

  const toggleExpand = (id: string): void => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  const handleDeleteType = async (id: string): Promise<void> => {
    setOpError(null)
    const res = await deleteType(id)
    if (!res.ok) setOpError(res.message)
    else if (expandedId === id) setExpandedId(null)
  }

  const handleDeleteValue = async (valueId: string): Promise<void> => {
    setOpError(null)
    const res = await deleteValue(valueId)
    if (!res.ok) setOpError(res.message)
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">Custom Data Types</h1>
        {can(SCREENCODES.MANAGECUSTOMDATATYPES, 'add') ? (
          <Button onClick={() => setShowAdd(true)}>Add</Button>
        ) : null}
      </header>

      {opError !== null ? <Alert tone="error">{opError}</Alert> : null}
      {error !== null ? (
        <Alert tone="error">
          {error}{' '}
          <button
            type="button"
            className="underline underline-offset-2 hover:text-charcoal"
            onClick={() => void reload()}
          >
            Retry
          </button>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <div className="space-y-3">
            {items.map((item) => (
              <CustomDataTypeCard
                key={item.id}
                item={item}
                expanded={expandedId === item.id}
                onToggle={() => toggleExpand(item.id)}
                onUpdateType={async (id, patch) => {
                  const res = await updateType(id, patch)
                  return res.ok
                    ? res
                    : { ok: false as const, message: res.message }
                }}
                onDeleteType={handleDeleteType}
                onAddValue={async (typeId, value) => {
                  const res = await addValue(typeId, value)
                  return res.ok
                    ? res
                    : { ok: false as const, message: res.message }
                }}
                onRenameValue={async (valueId, value) => {
                  const res = await renameValue(valueId, value)
                  return res.ok
                    ? res
                    : { ok: false as const, message: res.message }
                }}
                onDeleteValue={handleDeleteValue}
              />
            ))}
          </div>
          <Paginator
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </>
      )}

      <CustomDataTypeFormModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSubmit={async (type_name, is_axes) => createType(type_name, is_axes)}
      />
    </div>
  )
}

/**
 * EmptyState — purely informational, no duplicate Add button. The
 * Add button in the page header (top-right) is always present.
 * Matches the AttributesPage convention.
 */
function EmptyState(): JSX.Element {
  return (
    <div className="text-center py-16 border border-border rounded-md bg-parchment/40">
      <p className="font-heading text-h3 text-charcoal">No custom data types yet</p>
      <p className="font-body text-body-sm text-text-secondary mt-2">
        Use <span className="font-medium">Add</span> at the top right to declare
        the first one. Values come next, inside the expanded card.
      </p>
    </div>
  )
}
